import { useState, type FormEvent } from 'react';
import { Button, Callout, Field, Input } from '@biovet/ui';
import { ApiError, apiSend } from '../lib/api.js';

export function ChangePasswordScreen({
  fullName,
  onChanged,
  onSignOut,
}: {
  fullName: string;
  onChanged: () => void;
  onSignOut: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && next !== confirm;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatch || busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', '/password', { currentPassword: current, newPassword: next });
      onChanged();
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'Cannot reach the API. Try again in a moment.',
      );
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-y-auto bg-[var(--bg)] px-5 py-12">
      <div className="mx-auto flex w-full max-w-[400px] flex-col items-center">
        <header className="bb-rise-in flex flex-col items-center text-center">
          <div className="flex items-center gap-2.5">
            <div className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-[var(--accent-solid)] text-[15px] font-bold tracking-tight text-[var(--accent-ink)]">
              b
            </div>
            <div className="flex flex-col items-start leading-none">
              <span className="text-[16px] font-semibold tracking-tight">biovet</span>
              <span className="bb-eyebrow">Accounting</span>
            </div>
          </div>
        </header>

        <form
          onSubmit={(e) => void submit(e)}
          className="bb-rise-in mt-7 w-full rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-7 shadow-[var(--shadow)]"
          style={{ animationDelay: '0.08s' }}
        >
          <h2 className="m-0 text-[22px] font-semibold tracking-tight">Choose a password</h2>
          <p className="mt-2 mb-0 text-[12.5px] text-[var(--ink-2)]">
            {fullName}, the password you signed in with was issued to you and is meant to be used
            once. Pick one only you know.
          </p>

          <div className="flex flex-col gap-3.5 pt-6">
            <Field label="Password you signed in with" htmlFor="cp-current">
              <Input
                id="cp-current"
                type="password"
                autoComplete="current-password"
                value={current}
                required
                autoFocus
                onChange={(e) => setCurrent(e.target.value)}
              />
            </Field>

            <Field
              label="New password"
              htmlFor="cp-new"
              hint="At least ten characters. Length is what makes a password hard to guess."
            >
              <Input
                id="cp-new"
                type="password"
                autoComplete="new-password"
                value={next}
                required
                onChange={(e) => setNext(e.target.value)}
              />
            </Field>

            <Field
              label="New password again"
              htmlFor="cp-confirm"
              error={mismatch ? 'The two do not match.' : undefined}
            >
              <Input
                id="cp-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                required
                invalid={mismatch}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>
          </div>

          {error && (
            <div className="pt-4">
              <Callout tone="danger" title="That did not work">
                {error}
              </Callout>
            </div>
          )}

          <div className="pt-6">
            <Button type="submit" className="w-full" loading={busy} disabled={mismatch}>
              Set password and continue
            </Button>
          </div>

          <p className="mt-4 mb-0 text-[11.5px] text-[var(--ink-3)]">
            Any other session on this account is signed out when the password changes. If you did
            not expect to be asked for this,{' '}
            <button type="button" className="underline" onClick={onSignOut}>
              sign out
            </button>{' '}
            and speak to whoever administers the books.
          </p>
        </form>
      </div>
    </div>
  );
}
